/**
 * Data Utilities for Client-Side Filtering, Sorting, and Pagination
 * 
 * This module provides utility functions for handling data operations on the client side
 * after fetching all data from the API. This approach simplifies the API layer and
 * provides more flexibility for UI interactions.
 */

import { FilterOptions, PaginatedResult } from '@/models/config';

/**
 * Generic filter function that searches across multiple fields
 */
export function filterData<T>(
  data: T[],
  searchTerm: string,
  searchFields: (keyof T)[]
): T[] {
  if (!searchTerm.trim()) return data;
  
  const term = searchTerm.toLowerCase();
  return data.filter(item => 
    searchFields.some(field => {
      const value = item[field];
      if (value === null || value === undefined) return false;
      return String(value).toLowerCase().includes(term);
    })
  );
}

/**
 * Generic sort function
 */
export function sortData<T>(
  data: T[],
  sortBy: keyof T,
  sortOrder: 'asc' | 'desc' = 'asc'
): T[] {
  return [...data].sort((a, b) => {
    const aValue = a[sortBy];
    const bValue = b[sortBy];
    
    // Handle null/undefined values
    if (aValue === null || aValue === undefined) return sortOrder === 'asc' ? -1 : 1;
    if (bValue === null || bValue === undefined) return sortOrder === 'asc' ? 1 : -1;
    
    // Handle different data types
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortOrder === 'asc'
        ? aValue.localeCompare(bValue, undefined, { numeric: true, sensitivity: 'base' })
        : bValue.localeCompare(aValue, undefined, { numeric: true, sensitivity: 'base' });
    }
    
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
    }
    
    if (aValue instanceof Date && bValue instanceof Date) {
      return sortOrder === 'asc' 
        ? aValue.getTime() - bValue.getTime()
        : bValue.getTime() - aValue.getTime();
    }
    
    // Default string comparison
    const aStr = String(aValue);
    const bStr = String(bValue);
    return sortOrder === 'asc' 
      ? aStr.localeCompare(bStr)
      : bStr.localeCompare(aStr);
  });
}

/**
 * Generic pagination function
 */
export function paginateData<T>(
  data: T[],
  page: number = 1,
  limit: number = 50
): PaginatedResult<T> {
  const total = data.length;
  const totalPages = Math.ceil(total / limit);
  const startIndex = (page - 1) * limit;
  const endIndex = startIndex + limit;
  
  return {
    data: data.slice(startIndex, endIndex),
    total,
    page,
    limit,
    totalPages
  };
}

/**
 * Combined function that applies filtering, sorting, and pagination
 */
export function processData<T>(
  data: T[],
  options: FilterOptions,
  searchFields: (keyof T)[]
): PaginatedResult<T> {
  let processedData = [...data];
  
  // Apply search filter
  if (options.search) {
    processedData = filterData(processedData, options.search, searchFields);
  }
  
  // Apply sorting
  if (options.sortBy) {
    processedData = sortData(processedData, options.sortBy as keyof T, options.sortOrder);
  }
  
  // Apply pagination
  return paginateData(processedData, options.page, options.limit);
}

/**
 * Product-specific filtering and processing
 */
export function processProducts<T extends { item_code: string; item_name: string; is_deleted?: number }>(
  products: T[],
  options: FilterOptions
): PaginatedResult<T> {
  // Filter out deleted products first
  const activeProducts = products.filter(product => product.is_deleted !== 1);
  
  return processData(activeProducts, options, ['item_code', 'item_name']);
}

/**
 * Customer-specific filtering and processing
 */
export function processCustomers<T extends { name: string; company_name?: string; email?: string; is_deleted?: number }>(
  customers: T[],
  options: FilterOptions
): PaginatedResult<T> {
  // Filter out deleted customers first
  const activeCustomers = customers.filter(customer => customer.is_deleted !== 1);
  
  return processData(activeCustomers, options, ['name', 'company_name', 'email']);
}

/**
 * Invoice-specific filtering and processing
 */
export function processInvoices<T extends { 
  invoice_number: string; 
  customer_name?: string; 
  invoice_date?: string; 
  status?: string; 
  is_deleted?: number 
}>(
  invoices: T[],
  options: FilterOptions
): PaginatedResult<T> {
  // Filter out deleted invoices first
  const activeInvoices = invoices.filter(invoice => invoice.is_deleted !== 1);
  
  return processData(activeInvoices, options, ['invoice_number', 'customer_name', 'status']);
}

/**
 * User-specific filtering and processing
 */
export function processUsers<T extends { username: string; email: string; role?: string }>(
  users: T[],
  options: FilterOptions
): PaginatedResult<T> {
  return processData(users, options, ['username', 'email', 'role']);
}

/**
 * Advanced filtering with multiple conditions
 */
export function advancedFilter<T>(
  data: T[],
  filters: Record<string, any>
): T[] {
  return data.filter(item => {
    return Object.entries(filters).every(([key, value]) => {
      if (value === null || value === undefined || value === '') return true;
      
      const itemValue = item[key as keyof T];
      if (itemValue === null || itemValue === undefined) return false;
      
      // Handle different filter types
      if (typeof value === 'string') {
        return String(itemValue).toLowerCase().includes(value.toLowerCase());
      }
      
      if (typeof value === 'number') {
        return itemValue === value;
      }
      
      if (Array.isArray(value)) {
        return value.includes(itemValue);
      }
      
      return itemValue === value;
    });
  });
}

/**
 * Date range filtering
 */
export function filterByDateRange<T>(
  data: T[],
  dateField: keyof T,
  startDate?: string,
  endDate?: string
): T[] {
  if (!startDate && !endDate) return data;
  
  return data.filter(item => {
    const dateValue = item[dateField];
    if (!dateValue) return false;
    
    const itemDate = new Date(dateValue as any);
    
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      return itemDate >= start && itemDate <= end;
    }
    
    if (startDate) {
      const start = new Date(startDate);
      return itemDate >= start;
    }
    
    if (endDate) {
      const end = new Date(endDate);
      return itemDate <= end;
    }
    
    return true;
  });
}

/**
 * Export filtered data to CSV
 */
export function exportToCSV<T>(
  data: T[],
  headers: Record<keyof T, string>,
  filename: string = 'export.csv'
): void {
  if (data.length === 0) {
    console.warn('No data to export');
    return;
  }
  
  // Create CSV content
  const csvHeaders = Object.values(headers).join(',');
  const csvRows = data.map(item => 
    Object.keys(headers).map(key => {
      const value = item[key as keyof T];
      // Escape commas and quotes in CSV
      const stringValue = String(value || '');
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    }).join(',')
  );
  
  const csvContent = [csvHeaders, ...csvRows].join('\n');
  
  // Create and download file
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
} 